/**
 * MEVIA v4.0 - Organizational Structure Management Panel
 * Admin UI for managing areas, levels, positions, branches, and reporting lines
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  Building2, Users, Briefcase, MapPin, GitBranch,
  Plus, Edit2, Trash2, RefreshCw, Search, ChevronRight,
  ChevronDown, FolderTree, Layers, Building
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const TEXTS = {
  es: {
    title: 'Estructura Organizacional',
    subtitle: 'Gestiona áreas, niveles, puestos y sucursales',
    
    // Tabs
    areas: 'Áreas',
    levels: 'Niveles',
    positions: 'Puestos',
    branches: 'Sucursales',
    reporting: 'Líneas de Reporte',
    
    // Common
    search: 'Buscar...',
    add: 'Agregar',
    edit: 'Editar',
    delete: 'Eliminar',
    save: 'Guardar',
    cancel: 'Cancelar',
    refresh: 'Actualizar',
    code: 'Código',
    name: 'Nombre',
    nameEn: 'Nombre (EN)',
    description: 'Descripción',
    active: 'Activo',
    inactive: 'Inactivo',
    actions: 'Acciones',
    
    // Areas
    newArea: 'Nueva Área',
    editArea: 'Editar Área',
    parentArea: 'Área Padre',
    noParent: 'Sin padre (Raíz)',
    responsible: 'Responsable',
    color: 'Color',
    order: 'Orden',
    treeView: 'Vista Árbol',
    listView: 'Vista Lista',
    
    // Levels
    newLevel: 'Nuevo Nivel',
    editLevel: 'Editar Nivel',
    hierarchy: 'Jerarquía',
    hierarchyHelp: '1 = más alto (CEO), números mayores = menor jerarquía',
    canHaveSubordinates: 'Puede tener subordinados',
    canApprovePlans: 'Puede aprobar planes',
    
    // Positions
    newPosition: 'Nuevo Puesto',
    editPosition: 'Editar Puesto',
    area: 'Área',
    level: 'Nivel',
    requirements: 'Requisitos',
    responsibilities: 'Responsabilidades',
    salaryRange: 'Rango Salarial',
    minSalary: 'Mínimo',
    maxSalary: 'Máximo',
    headcount: 'Headcount Autorizado',
    
    // Branches
    newBranch: 'Nueva Sucursal',
    editBranch: 'Editar Sucursal',
    address: 'Dirección',
    city: 'Ciudad',
    state: 'Estado',
    country: 'País',
    phone: 'Teléfono',
    email: 'Email',
    isHeadquarters: 'Es Matriz',
    
    // Messages
    noItems: 'No hay registros',
    created: 'Creado exitosamente',
    updated: 'Actualizado exitosamente',
    deleted: 'Eliminado exitosamente',
    error: 'Error al procesar',
    confirmDelete: '¿Eliminar este registro?',
    deleteWarning: 'Esta acción desactivará el registro.',
    required: 'Campo requerido',
  },
  en: {
    title: 'Organizational Structure',
    subtitle: 'Manage areas, levels, positions, and branches',
    
    // Tabs
    areas: 'Areas',
    levels: 'Levels',
    positions: 'Positions',
    branches: 'Branches',
    reporting: 'Reporting Lines',
    
    // Common
    search: 'Search...',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    refresh: 'Refresh',
    code: 'Code',
    name: 'Name',
    nameEn: 'Name (EN)',
    description: 'Description',
    active: 'Active',
    inactive: 'Inactive',
    actions: 'Actions',
    
    // Areas
    newArea: 'New Area',
    editArea: 'Edit Area',
    parentArea: 'Parent Area',
    noParent: 'No parent (Root)',
    responsible: 'Responsible',
    color: 'Color',
    order: 'Order',
    treeView: 'Tree View',
    listView: 'List View',
    
    // Levels
    newLevel: 'New Level',
    editLevel: 'Edit Level',
    hierarchy: 'Hierarchy',
    hierarchyHelp: '1 = highest (CEO), higher numbers = lower hierarchy',
    canHaveSubordinates: 'Can have subordinates',
    canApprovePlans: 'Can approve plans',
    
    // Positions
    newPosition: 'New Position',
    editPosition: 'Edit Position',
    area: 'Area',
    level: 'Level',
    requirements: 'Requirements',
    responsibilities: 'Responsibilities',
    salaryRange: 'Salary Range',
    minSalary: 'Minimum',
    maxSalary: 'Maximum',
    headcount: 'Authorized Headcount',
    
    // Branches
    newBranch: 'New Branch',
    editBranch: 'Edit Branch',
    address: 'Address',
    city: 'City',
    state: 'State',
    country: 'Country',
    phone: 'Phone',
    email: 'Email',
    isHeadquarters: 'Is Headquarters',
    
    // Messages
    noItems: 'No records',
    created: 'Created successfully',
    updated: 'Updated successfully',
    deleted: 'Deleted successfully',
    error: 'Error processing',
    confirmDelete: 'Delete this record?',
    deleteWarning: 'This action will deactivate the record.',
    required: 'Required field',
  }
};

// Tree Node Component
const TreeNode = ({ node, level = 0, onEdit, onDelete, texts }) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="select-none">
      <div 
        className={`flex items-center gap-2 py-2 px-3 hover:bg-gray-50 rounded-lg cursor-pointer`}
        style={{ paddingLeft: `${level * 24 + 12}px` }}
      >
        {hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-gray-200 rounded">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="w-6" />
        )}
        
        <div 
          className="w-3 h-3 rounded-full border-2" 
          style={{ backgroundColor: node.color || '#1A878D', borderColor: node.color || '#1A878D' }}
        />
        
        <div className="flex-1">
          <span className="font-medium">{node.nombre}</span>
          <span className="text-gray-400 text-sm ml-2">({node.codigo})</span>
        </div>
        
        <Badge variant={node.activo ? "success" : "secondary"} className="text-xs">
          {node.activo ? texts.active : texts.inactive}
        </Badge>
        
        <div className="flex gap-1 ml-2">
          <Button variant="ghost" size="sm" onClick={() => onEdit(node)}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(node)}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>
      
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TreeNode 
              key={child.id} 
              node={child} 
              level={level + 1} 
              onEdit={onEdit}
              onDelete={onDelete}
              texts={texts}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const OrganizationalStructure = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const texts = TEXTS[language] || TEXTS.es;
  const empresaId = user?.empresa_id;

  const [activeTab, setActiveTab] = useState('areas');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [areas, setAreas] = useState([]);
  const [areasTree, setAreasTree] = useState([]);
  const [levels, setLevels] = useState([]);
  const [positions, setPositions] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // View states
  const [treeView, setTreeView] = useState(true);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogType, setDialogType] = useState('area'); // area, level, position, branch

  // Form states
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (empresaId) {
      initializeTables();
      loadAllData();
    }
  }, [empresaId]);

  const initializeTables = async () => {
    try {
      await axios.post('/organization/init');
    } catch (error) {
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadAreas(),
        loadLevels(),
        loadPositions(),
        loadBranches()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadAreas = async () => {
    try {
      const [listRes, treeRes] = await Promise.all([
        axios.get(`/organization/areas/${empresaId}?include_inactive=true`),
        axios.get(`/organization/areas/${empresaId}/tree`)
      ]);
      if (listRes.data.success) setAreas(listRes.data.data || []);
      if (treeRes.data.success) setAreasTree(treeRes.data.data || []);
    } catch (error) {
      logger.error('Error loading areas:', error);
    }
  };

  const loadLevels = async () => {
    try {
      const response = await axios.get(`/organization/niveles/${empresaId}?include_inactive=true`);
      if (response.data.success) setLevels(response.data.data || []);
    } catch (error) {
      logger.error('Error loading levels:', error);
    }
  };

  const loadPositions = async () => {
    try {
      const response = await axios.get(`/organization/puestos/${empresaId}?include_inactive=true`);
      if (response.data.success) setPositions(response.data.data || []);
    } catch (error) {
      logger.error('Error loading positions:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const response = await axios.get(`/organization/sucursales/${empresaId}?include_inactive=true`);
      if (response.data.success) setBranches(response.data.data || []);
    } catch (error) {
      logger.error('Error loading branches:', error);
    }
  };

  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditingItem(item);
    
    if (item) {
      setFormData({ ...item });
    } else {
      // Default values for new items
      const defaults = {
        area: { codigo: '', nombre: '', nombre_en: '', descripcion: '', area_padre_id: '', color: '#1A878D', orden: 0 },
        level: { codigo: '', nombre: '', nombre_en: '', descripcion: '', jerarquia: 5, puede_tener_subordinados: true, puede_aprobar_planes: false, color: '#1A878D' },
        position: { codigo: '', nombre: '', nombre_en: '', descripcion: '', area_id: '', nivel_id: '', requisitos: '', responsabilidades: '', headcount_autorizado: 1 },
        branch: { codigo: '', nombre: '', direccion: '', ciudad: '', estado_codigo: '', pais_codigo: 'MX', telefono: '', email: '', es_matriz: false }
      };
      setFormData(defaults[type] || {});
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.codigo || !formData.nombre) {
      toast.error(texts.required);
      return;
    }

    const endpoints = {
      area: 'areas',
      level: 'niveles',
      position: 'puestos',
      branch: 'sucursales'
    };

    try {
      const endpoint = endpoints[dialogType];
      
      // Clean up form data
      const cleanData = { ...formData };
      delete cleanData.id;
      delete cleanData.empresa_id;
      delete cleanData.created_at;
      delete cleanData.updated_at;
      delete cleanData.activo;
      delete cleanData.area_nombre;
      delete cleanData.nivel_nombre;
      delete cleanData.total_empleados;
      delete cleanData.headcount_actual;
      
      // Remove empty string values for optional UUID fields
      if (cleanData.area_padre_id === '' || cleanData.area_padre_id === 'none') delete cleanData.area_padre_id;
      if (cleanData.responsable_id === '' || cleanData.responsable_id === 'none') delete cleanData.responsable_id;
      if (cleanData.area_id === '' || cleanData.area_id === 'none') delete cleanData.area_id;
      if (cleanData.nivel_id === '' || cleanData.nivel_id === 'none') delete cleanData.nivel_id;

      if (editingItem) {
        await axios.put(`/organization/${endpoint}/${empresaId}/${editingItem.id}`, cleanData);
        toast.success(texts.updated);
      } else {
        await axios.post(`/organization/${endpoint}/${empresaId}`, cleanData);
        toast.success(texts.created);
      }
      
      setDialogOpen(false);
      loadAllData();
    } catch (error) {
      logger.error('Error saving:', error);
      toast.error(error.response?.data?.detail || texts.error);
    }
  };

  const handleDelete = async () => {
    if (!editingItem) return;

    const endpoints = {
      area: 'areas',
      level: 'niveles',
      position: 'puestos',
      branch: 'sucursales'
    };

    try {
      const endpoint = endpoints[dialogType];
      await axios.delete(`/organization/${endpoint}/${empresaId}/${editingItem.id}`);
      toast.success(texts.deleted);
      setDeleteDialogOpen(false);
      setEditingItem(null);
      loadAllData();
    } catch (error) {
      logger.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || texts.error);
    }
  };

  const confirmDelete = (type, item) => {
    setDialogType(type);
    setEditingItem(item);
    setDeleteDialogOpen(true);
  };

  const filteredAreas = areas.filter(a => 
    a.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.codigo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLevels = levels.filter(l =>
    l.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.codigo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPositions = positions.filter(p =>
    p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.codigo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredBranches = branches.filter(b =>
    b.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.codigo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="areas" className="gap-2">
              <FolderTree className="h-4 w-4" />
              {texts.areas}
            </TabsTrigger>
            <TabsTrigger value="levels" className="gap-2">
              <Layers className="h-4 w-4" />
              {texts.levels}
            </TabsTrigger>
            <TabsTrigger value="positions" className="gap-2">
              <Briefcase className="h-4 w-4" />
              {texts.positions}
            </TabsTrigger>
            <TabsTrigger value="branches" className="gap-2">
              <Building className="h-4 w-4" />
              {texts.branches}
            </TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={texts.search}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-48"
              />
            </div>
            <Button variant="outline" size="sm" onClick={loadAllData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Areas Tab */}
        <TabsContent value="areas">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FolderTree className="h-5 w-5 text-teal-600" />
                    {texts.areas}
                  </CardTitle>
                  <CardDescription>
                    {language === 'es' ? 'Estructura jerárquica de departamentos' : 'Hierarchical department structure'}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={treeView ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTreeView(true)}
                  >
                    {texts.treeView}
                  </Button>
                  <Button
                    variant={!treeView ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTreeView(false)}
                  >
                    {texts.listView}
                  </Button>
                  <Button size="sm" onClick={() => openDialog('area')}>
                    <Plus className="h-4 w-4 mr-2" />
                    {texts.add}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {treeView ? (
                areasTree.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FolderTree className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>{texts.noItems}</p>
                  </div>
                ) : (
                  <div className="border rounded-lg p-2">
                    {areasTree.map((node) => (
                      <TreeNode 
                        key={node.id} 
                        node={node} 
                        onEdit={(item) => openDialog('area', item)}
                        onDelete={(item) => confirmDelete('area', item)}
                        texts={texts}
                      />
                    ))}
                  </div>
                )
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{texts.code}</TableHead>
                      <TableHead>{texts.name}</TableHead>
                      <TableHead>{texts.parentArea}</TableHead>
                      <TableHead>{texts.active}</TableHead>
                      <TableHead>{texts.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAreas.map((area) => (
                      <TableRow key={area.id}>
                        <TableCell className="font-mono">{area.codigo}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: area.color || '#1A878D' }}
                            />
                            {area.nombre}
                          </div>
                        </TableCell>
                        <TableCell>{area.area_padre_nombre || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={area.activo ? "success" : "secondary"}>
                            {area.activo ? texts.active : texts.inactive}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('area', area)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => confirmDelete('area', area)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Levels Tab */}
        <TabsContent value="levels">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="h-5 w-5 text-teal-600" />
                    {texts.levels}
                  </CardTitle>
                  <CardDescription>
                    {language === 'es' ? 'Niveles jerárquicos de la organización' : 'Organizational hierarchy levels'}
                  </CardDescription>
                </div>
                <Button size="sm" onClick={() => openDialog('level')}>
                  <Plus className="h-4 w-4 mr-2" />
                  {texts.add}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredLevels.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Layers className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{texts.noItems}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{texts.code}</TableHead>
                      <TableHead>{texts.name}</TableHead>
                      <TableHead>{texts.hierarchy}</TableHead>
                      <TableHead>{texts.canHaveSubordinates}</TableHead>
                      <TableHead>{texts.canApprovePlans}</TableHead>
                      <TableHead>{texts.active}</TableHead>
                      <TableHead>{texts.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLevels.sort((a, b) => a.jerarquia - b.jerarquia).map((level) => (
                      <TableRow key={level.id}>
                        <TableCell className="font-mono">{level.codigo}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: level.color || '#1A878D' }}
                            />
                            {level.nombre}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{level.jerarquia}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={level.puede_tener_subordinados ? "success" : "secondary"}>
                            {level.puede_tener_subordinados ? 'Sí' : 'No'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={level.puede_aprobar_planes ? "success" : "secondary"}>
                            {level.puede_aprobar_planes ? 'Sí' : 'No'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={level.activo ? "success" : "secondary"}>
                            {level.activo ? texts.active : texts.inactive}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('level', level)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => confirmDelete('level', level)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Positions Tab */}
        <TabsContent value="positions">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-teal-600" />
                    {texts.positions}
                  </CardTitle>
                  <CardDescription>
                    {language === 'es' ? 'Catálogo de puestos de trabajo' : 'Job positions catalog'}
                  </CardDescription>
                </div>
                <Button size="sm" onClick={() => openDialog('position')}>
                  <Plus className="h-4 w-4 mr-2" />
                  {texts.add}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredPositions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{texts.noItems}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{texts.code}</TableHead>
                      <TableHead>{texts.name}</TableHead>
                      <TableHead>{texts.area}</TableHead>
                      <TableHead>{texts.level}</TableHead>
                      <TableHead>{texts.headcount}</TableHead>
                      <TableHead>{texts.active}</TableHead>
                      <TableHead>{texts.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPositions.map((pos) => (
                      <TableRow key={pos.id}>
                        <TableCell className="font-mono">{pos.codigo}</TableCell>
                        <TableCell>{pos.nombre}</TableCell>
                        <TableCell>{pos.area_nombre || '-'}</TableCell>
                        <TableCell>{pos.nivel_nombre || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{pos.headcount_autorizado || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={pos.activo ? "success" : "secondary"}>
                            {pos.activo ? texts.active : texts.inactive}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('position', pos)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => confirmDelete('position', pos)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branches Tab */}
        <TabsContent value="branches">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-teal-600" />
                    {texts.branches}
                  </CardTitle>
                  <CardDescription>
                    {language === 'es' ? 'Sucursales y ubicaciones' : 'Branches and locations'}
                  </CardDescription>
                </div>
                <Button size="sm" onClick={() => openDialog('branch')}>
                  <Plus className="h-4 w-4 mr-2" />
                  {texts.add}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredBranches.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{texts.noItems}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{texts.code}</TableHead>
                      <TableHead>{texts.name}</TableHead>
                      <TableHead>{texts.city}</TableHead>
                      <TableHead>{texts.isHeadquarters}</TableHead>
                      <TableHead>{texts.active}</TableHead>
                      <TableHead>{texts.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBranches.map((branch) => (
                      <TableRow key={branch.id}>
                        <TableCell className="font-mono">{branch.codigo}</TableCell>
                        <TableCell>{branch.nombre}</TableCell>
                        <TableCell>{branch.ciudad || '-'}</TableCell>
                        <TableCell>
                          {branch.es_matriz && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              {texts.isHeadquarters}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={branch.activo ? "success" : "secondary"}>
                            {branch.activo ? texts.active : texts.inactive}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('branch', branch)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => confirmDelete('branch', branch)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? texts.edit : texts.add} - {
                dialogType === 'area' ? texts.areas :
                dialogType === 'level' ? texts.levels :
                dialogType === 'position' ? texts.positions :
                texts.branches
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Common fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">{texts.code} *</label>
                <Input
                  value={formData.codigo || ''}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                  placeholder="CODE"
                  maxLength={20}
                />
              </div>
              {dialogType !== 'branch' && (
                <div>
                  <label className="text-sm font-medium mb-1 block">{texts.color}</label>
                  <Input
                    type="color"
                    value={formData.color || '#1A878D'}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="h-10"
                  />
                </div>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">{texts.name} *</label>
              <Input
                value={formData.nombre || ''}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
            </div>

            {dialogType !== 'branch' && (
              <div>
                <label className="text-sm font-medium mb-1 block">{texts.nameEn}</label>
                <Input
                  value={formData.nombre_en || ''}
                  onChange={(e) => setFormData({ ...formData, nombre_en: e.target.value })}
                />
              </div>
            )}

            {/* Area-specific fields */}
            {dialogType === 'area' && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">{texts.parentArea}</label>
                  <Select 
                    value={formData.area_padre_id || 'none'} 
                    onValueChange={(v) => setFormData({ ...formData, area_padre_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={texts.noParent} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{texts.noParent}</SelectItem>
                      {areas.filter(a => a.id !== editingItem?.id && a.activo).map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">{texts.order}</label>
                  <Input
                    type="number"
                    value={formData.orden || 0}
                    onChange={(e) => setFormData({ ...formData, orden: parseInt(e.target.value) || 0 })}
                    min={0}
                  />
                </div>
              </>
            )}

            {/* Level-specific fields */}
            {dialogType === 'level' && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">{texts.hierarchy}</label>
                  <Input
                    type="number"
                    value={formData.jerarquia || 5}
                    onChange={(e) => setFormData({ ...formData, jerarquia: parseInt(e.target.value) || 1 })}
                    min={1}
                    max={20}
                  />
                  <p className="text-xs text-gray-500 mt-1">{texts.hierarchyHelp}</p>
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">{texts.canHaveSubordinates}</label>
                  <input
                    type="checkbox"
                    checked={formData.puede_tener_subordinados !== false}
                    onChange={(e) => setFormData({ ...formData, puede_tener_subordinados: e.target.checked })}
                    className="h-4 w-4"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">{texts.canApprovePlans}</label>
                  <input
                    type="checkbox"
                    checked={formData.puede_aprobar_planes === true}
                    onChange={(e) => setFormData({ ...formData, puede_aprobar_planes: e.target.checked })}
                    className="h-4 w-4"
                  />
                </div>
              </>
            )}

            {/* Position-specific fields */}
            {dialogType === 'position' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">{texts.area}</label>
                    <Select 
                      value={formData.area_id || 'none'} 
                      onValueChange={(v) => setFormData({ ...formData, area_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="-" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-</SelectItem>
                        {areas.filter(a => a.activo).map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">{texts.level}</label>
                    <Select 
                      value={formData.nivel_id || 'none'} 
                      onValueChange={(v) => setFormData({ ...formData, nivel_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="-" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-</SelectItem>
                        {levels.filter(l => l.activo).map(l => (
                          <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">{texts.headcount}</label>
                  <Input
                    type="number"
                    value={formData.headcount_autorizado || 1}
                    onChange={(e) => setFormData({ ...formData, headcount_autorizado: parseInt(e.target.value) || 0 })}
                    min={0}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">{texts.requirements}</label>
                  <Textarea
                    value={formData.requisitos || ''}
                    onChange={(e) => setFormData({ ...formData, requisitos: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">{texts.responsibilities}</label>
                  <Textarea
                    value={formData.responsabilidades || ''}
                    onChange={(e) => setFormData({ ...formData, responsabilidades: e.target.value })}
                    rows={2}
                  />
                </div>
              </>
            )}

            {/* Branch-specific fields */}
            {dialogType === 'branch' && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">{texts.address}</label>
                  <Input
                    value={formData.direccion || ''}
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">{texts.city}</label>
                    <Input
                      value={formData.ciudad || ''}
                      onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">{texts.state}</label>
                    <Input
                      value={formData.estado_codigo || ''}
                      onChange={(e) => setFormData({ ...formData, estado_codigo: e.target.value })}
                      maxLength={10}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">{texts.phone}</label>
                    <Input
                      value={formData.telefono || ''}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">{texts.email}</label>
                    <Input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">{texts.isHeadquarters}</label>
                  <input
                    type="checkbox"
                    checked={formData.es_matriz === true}
                    onChange={(e) => setFormData({ ...formData, es_matriz: e.target.checked })}
                    className="h-4 w-4"
                  />
                </div>
              </>
            )}

            {/* Description (common) */}
            {dialogType !== 'branch' && (
              <div>
                <label className="text-sm font-medium mb-1 block">{texts.description}</label>
                <Textarea
                  value={formData.descripcion || ''}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  rows={2}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {texts.cancel}
            </Button>
            <Button onClick={handleSave}>
              {texts.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{texts.confirmDelete}</DialogTitle>
            <DialogDescription>{texts.deleteWarning}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {texts.cancel}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {texts.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationalStructure;
